import React, { useState, useEffect } from 'react'
import { X, ChevronDown, ChevronUp } from 'lucide-react'
import { aiStudioService } from 'service/aiStudio'
import { convertStorageUrlToCdn } from 'utils/common'

interface FullImageModalProps {
  isOpen: boolean
  onClose: () => void
  id: number | null
}

export const FullImageModal: React.FC<FullImageModalProps> = ({
  isOpen,
  onClose,
  id,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [data, setData] = useState<any>(null)
  const [error, setError] = useState<string | null>(null)
  const [showDetails, setShowDetails] = useState(false)

  useEffect(() => {
    if (isOpen && id) {
      setIsLoading(true)
      setError(null)
      setData(null)

      aiStudioService
        .getSynthesisJobFull(id)
        .then((response) => {
          setData(response)
          setIsLoading(false)
        })
        .catch((error) => {
          console.error('Full image data fetch failed:', error)
          setError('데이터를 불러오는데 실패했습니다.')
          setIsLoading(false)
        })
    }
  }, [isOpen, id])

  if (!isOpen) return null

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const getImageLabels = () => {
    return ['아바타 이미지', '의상 이미지', '배경 이미지']
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[70]"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-[20px] max-w-6xl max-h-[90vh] w-[95vw] overflow-hidden">
        {/* 헤더 */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold text-[#2E343D]">
            이미지 생성 상세 정보 (id: {id})
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={24} className="text-gray-600" />
          </button>
        </div>

        {/* 내용 */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)] space-y-6">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00B7B9]"></div>
              <span className="ml-3 text-gray-600">
                데이터를 불러오는 중...
              </span>
            </div>
          )}

          {error && (
            <div className="text-center py-12">
              <p className="text-red-500 text-lg">{error}</p>
            </div>
          )}

          {data && !isLoading && !error && (
            <>
              {/* 프롬프트 섹션 */}
              {data.prompt && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3 text-[#2E343D]">
                    AI 생성 프롬프트
                  </h3>
                  <textarea
                    value={data.prompt}
                    readOnly
                    className="w-full h-48 p-4 bg-white border rounded-lg text-sm font-mono resize-none overflow-y-auto"
                    style={{ whiteSpace: 'pre-wrap' }}
                  />
                </div>
              )}

              {/* 입력 이미지들 섹션 */}
              {data.image_urls && data.image_urls.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3 text-[#2E343D]">
                    입력 이미지들
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {data.image_urls.map((url: string, index: number) => (
                      <div key={index} className="bg-white rounded-lg p-3">
                        <div className="text-sm font-medium text-gray-700 mb-2">
                          {getImageLabels()[index] || `이미지 ${index + 1}`}
                        </div>
                        <div className="aspect-[9/16] rounded-lg overflow-hidden bg-gray-100">
                          <img
                            src={convertStorageUrlToCdn(url)}
                            alt={`입력 이미지 ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 결과 이미지 섹션 */}
              {data.result_urls && data.result_urls.length > 0 && (
                <div className="bg-green-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3 text-[#2E343D]">
                    생성 결과
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    위 이미지들을 AI가 합성하여 생성한 결과입니다
                  </p>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* 결과 이미지 */}
                    <div className="lg:col-span-1">
                      {data.result_urls.map((url: string, index: number) => (
                        <div key={index} className="bg-white rounded-lg p-3">
                          <div className="text-sm font-medium text-gray-700 mb-2">
                            결과 이미지 {index + 1}
                          </div>
                          <div className="aspect-[9/16] rounded-lg overflow-hidden bg-gray-100">
                            <img
                              src={convertStorageUrlToCdn(url)}
                              alt={`결과 이미지 ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* DB 정보 */}
                    {data.db_info && (
                      <div className="lg:col-span-2 space-y-4">
                        {/* 포즈 정보 */}
                        {data.db_info.pose && (
                          <div className="bg-white rounded-lg p-4">
                            <h4 className="text-sm font-semibold mb-2 text-[#2E343D]">
                              포즈 정보
                            </h4>
                            <div className="space-y-2 text-sm">
                              <div>
                                <span className="font-medium text-gray-700">
                                  이름:
                                </span>
                                <span className="ml-2">
                                  {data.db_info.pose.name}
                                </span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-700">
                                  번호:
                                </span>
                                <span className="ml-2">
                                  {data.db_info.pose?.description}
                                </span>
                              </div>

                              <div>
                                <span className="font-medium text-gray-700">
                                  설명:
                                </span>
                                <div className="mt-1 p-2 bg-gray-50 rounded text-xs">
                                  {data.db_info.pose.poseScript}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* 배경 정보 */}
                        {data.db_info.background && (
                          <div className="bg-white rounded-lg p-4">
                            <h4 className="text-sm font-semibold mb-2 text-[#2E343D]">
                              배경 정보
                            </h4>
                            <div className="space-y-2 text-sm">
                              <div>
                                <span className="font-medium text-gray-700">
                                  이름:
                                </span>
                                <span className="ml-2">
                                  {data.db_info.background.name}
                                </span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-700">
                                  타입:
                                </span>
                                <span className="ml-2">
                                  {data.db_info.background.type}
                                </span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-700">
                                  설명:
                                </span>
                                <span className="ml-2">
                                  {data.db_info.background.description}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* 아바타 정보 */}
                        {data.db_info.avatar && (
                          <div className="bg-white rounded-lg p-4">
                            <h4 className="text-sm font-semibold mb-2 text-[#2E343D]">
                              아바타 정보
                            </h4>
                            <div className="space-y-2 text-sm">
                              <div>
                                <span className="font-medium text-gray-700">
                                  이름:
                                </span>
                                <span className="ml-2">
                                  {data.db_info.avatar.name}
                                </span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-700">
                                  성별:
                                </span>
                                <span className="ml-2">
                                  {data.db_info.avatar.gender === 'female'
                                    ? '여성'
                                    : '남성'}
                                </span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-700">
                                  신체 사양:
                                </span>
                                <div className="mt-1 p-2 bg-gray-50 rounded text-xs">
                                  {data.db_info.avatar.bodySpec}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* 작업 정보 */}
                        <div className="bg-white rounded-lg p-4">
                          <h4 className="text-sm font-semibold mb-2 text-[#2E343D]">
                            작업 정보
                          </h4>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <div>
                                <span className="font-medium text-gray-700">
                                  작업 ID:
                                </span>
                                <span className="ml-2">{data.db_info.id}</span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-700">
                                  요청 ID:
                                </span>
                                <span className="ml-2">
                                  {data.db_info.userRequestId}
                                </span>
                              </div>
                            </div>
                            <div>
                              <div>
                                <span className="font-medium text-gray-700">
                                  처리 시도:
                                </span>
                                <span className="ml-2">
                                  {data.db_info.processingAttempts}/
                                  {data.db_info.maxAttempts}
                                </span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-700">
                                  상태:
                                </span>
                                <span
                                  className={`ml-2 px-2 py-0.5 rounded text-xs ${
                                    data.db_info.status === 'completed'
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-yellow-100 text-yellow-800'
                                  }`}
                                >
                                  {data.db_info.status === 'completed'
                                    ? '완료'
                                    : data.db_info.status}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* 기본 정보 섹션 */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-3 text-[#2E343D]">
                  기본 정보
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm font-medium text-gray-700">
                        상태:
                      </span>
                      <span
                        className={`ml-2 px-2 py-1 rounded text-xs font-medium ${
                          data.status === 'completed'
                            ? 'bg-green-100 text-green-800'
                            : data.status === 'processing'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {data.status === 'completed'
                          ? '완료'
                          : data.status === 'processing'
                          ? '처리중'
                          : data.status}
                      </span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-700">
                        진행률:
                      </span>
                      <span className="ml-2 text-sm">{data.progress}%</span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-700">
                        모델:
                      </span>
                      <span className="ml-2 text-sm">{data.model}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm font-medium text-gray-700">
                        생성 시작:
                      </span>
                      <span className="ml-2 text-sm">
                        {data.created_at
                          ? new Date(data.created_at).toLocaleString('ko-KR')
                          : '-'}
                      </span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-700">
                        완료 시간:
                      </span>
                      <span className="ml-2 text-sm">
                        {data.updated_at
                          ? new Date(data.updated_at).toLocaleString('ko-KR')
                          : '-'}
                      </span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-700">
                        화면 비율:
                      </span>
                      <span className="ml-2 text-sm">
                        {data.aspect_ratio || '9:16'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 상세 정보 (토글) */}
              <div className="bg-gray-50 rounded-lg">
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="w-full p-4 flex justify-between items-center text-left hover:bg-gray-100 transition-colors rounded-lg"
                >
                  <h3 className="text-lg font-semibold text-[#2E343D]">
                    상세 정보 (개발자용)
                  </h3>
                  {showDetails ? (
                    <ChevronUp size={20} />
                  ) : (
                    <ChevronDown size={20} />
                  )}
                </button>
                {showDetails && (
                  <div className="px-4 pb-4">
                    <div className="bg-white p-4 rounded border">
                      <h4 className="text-sm font-semibold mb-2 text-gray-700">
                        원본 API 응답 데이터
                      </h4>
                      <pre className="text-xs bg-gray-50 p-3 rounded border overflow-x-auto max-h-96 overflow-y-auto">
                        {JSON.stringify(data, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
